import { useCallback } from 'react';
import { create } from 'zustand';
import { getOptionManufactures } from 'services/option';
import { unionBy } from 'lodash';

// ======================
// Types
// ======================

export interface Manufacture {
  label: string;
  value: string;
}

interface RawManufacture {
  id: number | string;
  name: string;
}

interface ManufactureStore {
  manufacturers: Manufacture[];
  total: number;
  page: number;
  setManufactures: (manufactures: Manufacture[], total: number) => void;
  appendManufactures: (newManufactures: Manufacture[], total: number, page?: number) => void;
}

// ======================
// Store
// ======================

export const useManufactureStore = create<ManufactureStore>((set) => ({
  manufacturers: [],
  total: 0,
  page: 1,
  setManufactures: (manufacturers, total) => set({ manufacturers, total }),
  appendManufactures: (newManufactures, total, page) => {
    set((state) => ({
      manufacturers: unionBy([...state.manufacturers, ...newManufactures], 'value'),
      total,
      page: page || 1
    }));
  }
}));

// ======================
// Helpers
// ======================

const mapManufactureResponse = (raw: RawManufacture[]): Manufacture[] => {
  return raw.map((item) => ({
    label: item.name,
    value: String(item.id)
  }));
};

// ======================
// Hooks
// ======================

/**
 * Hook to fetch first page of manufacturers
 */
export const useFetchManufactures = () => {
  const { setManufactures } = useManufactureStore();

  const init = useCallback(async () => {
    try {
      const data = await getOptionManufactures();
      const options = mapManufactureResponse(data.manufacturers);
      options.unshift({ label: 'OTHER', value: 'OTHER' });
      setManufactures(options, data.total);
    } catch (error) {
      console.error('Failed to fetch manufactures:', error);
      // Optional: toast or error handling
    }
  }, [setManufactures]);

  return init;
};

/**
 * Hook to fetch next page of manufacturers (infinite scroll / load more)
 */
export const useFetchMoreManufactures = () => {
  const { manufacturers, total, page, appendManufactures } = useManufactureStore();

  const init = useCallback(async () => {
    if (manufacturers.length >= total) return true;
    const newPage = page + 1;
    try {
      const data = await getOptionManufactures({ page: newPage }); // support pagination
      const options = mapManufactureResponse(data.manufacturers);

      appendManufactures(options, data.total, newPage);
    } catch (error) {
      console.error('Failed to fetch more manufactures:', error);
    }
  }, [manufacturers.length, total, page, appendManufactures]);

  return init;
};

export const useInsertOptionManufacturer = () => {
  const insert = useCallback((option: Manufacture) => {
    const { manufacturers, total, setManufactures } = useManufactureStore.getState();
    const newManufacturer = unionBy(manufacturers, [option], 'value');

    setManufactures(newManufacturer, total);
  }, []);

  return insert;
};
